import {skipToken} from '@reduxjs/toolkit/query'
import {useContext, useEffect, useMemo} from 'react'

import {useBuildData, useProjectData} from '../../../../hooks/useEntity'
import {useThrottle} from '../../../../hooks/useThrottle'
import {getExtensionEndpoint} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {TestOccurrence, GetTestOccurrenceTreeApiArg} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {TEST_MUTE_UPDATED, TEST_RESPONSIBILITY_CHANGED} from '../../../../utils/subscriptionEvents'
import {getFlakyTestDataByBuildHash} from '../Tests.selectors'

import {requestTestOccurrenceTreeOptions} from './TestsTree.constants'
import {TestsTreeContext} from './TestsTree.context'

import {useAppSelector} from 'src/hooks/react-redux'

const THROTTLER_TIMEOUT = 5000

export function useTestOccurrenceTreeArg(locator: string): GetTestOccurrenceTreeApiArg {
  const {locatorInEndpoint, getEndpoint} = useContext(TestsTreeContext)

  return useMemo(
    () => ({
      endpoint: getEndpoint(locator),
      locator: locatorInEndpoint ? null : locator,
      options: requestTestOccurrenceTreeOptions,
      treeLocator: locator,
    }),
    [getEndpoint, locator, locatorInEndpoint],
  )
}

export const useFetchTreeData = (locator: string, buildId?: BuildId) => {
  const count = useBuildData({buildId}, build => build?.testOccurrences?.count)
  const projectInternalId = useProjectData({buildId}, project => project?.internalId)

  const arg = useTestOccurrenceTreeArg(locator)
  const {treeLeaves, refetch} = restApi.endpoints.getTestOccurrenceTree.useQuery(arg, {
    selectFromResult: ({data}) => ({treeLeaves: data?.tree.leaf ?? emptyArray}),
  })

  const fetchTestOccurrenceTreeHandler = useThrottle(refetch, THROTTLER_TIMEOUT)

  useEffect(() => {
    if (projectInternalId != null) {
      return subscribeOnProjectEvents(
        projectInternalId,
        [TEST_RESPONSIBILITY_CHANGED, TEST_MUTE_UPDATED],
        fetchTestOccurrenceTreeHandler,
      )
    }
    return () => undefined
  }, [projectInternalId, fetchTestOccurrenceTreeHandler])

  useEffect(() => {
    fetchTestOccurrenceTreeHandler()
  }, [count, fetchTestOccurrenceTreeHandler])

  const testOccurrences = useMemo(
    () =>
      treeLeaves.reduce<TestOccurrence[]>(
        (acc, leaf) => [...acc, ...(leaf.testOccurrences?.testOccurrence ?? [])],
        [],
      ),
    [treeLeaves],
  )
  const flakyTests = useAppSelector(state => getFlakyTestDataByBuildHash(state, buildId))
  const flakyTestsEndpoint = useAppSelector(state => getExtensionEndpoint(state, 'flaky'))

  const tests = testOccurrences
    .map(testOccurrence => ({
      testName: testOccurrence.test?.id,
      buildTypeId: testOccurrence.build?.buildType?.internalId,
    }))
    .filter(test => test.testName != null && test.buildTypeId != null && !flakyTests[test.testName])
  restApi.endpoints.getFlakyTests.useQuerySubscription(
    buildId != null && tests.length > 0 && flakyTestsEndpoint != null
      ? {
          buildId,
          tests,
          endpoint: flakyTestsEndpoint.endpoint,
        }
      : skipToken,
  )
}
